import React, { useEffect, useId, useState } from 'react';
import { X } from 'lucide-react';
import { Reveal } from '../Reveal';
import { SectionBand, Eyebrow } from './SectionBand';
import { DeviceFrame } from './DeviceFrame';
import { ProofOfBuild } from './ProofOfBuild';
const steps = [{
  n: '1',
  title: 'Find what applies',
  caption: 'The reader identifies where the book connects to their situation.',
  src: "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/4wv71qfbbbwbngbvre9mbk/67ed1325-767e-4f35-bd1c-b49fa7a310ff/02_diagnostic_starting_point.png",
  chromeLabel: 'Prototype · Find what applies',
  frameLine: 'This is where a reader stops skimming and starts recognizing their own situation.',
  aspect: '4 / 3',
  eager: true
}, {
  n: '2',
  title: 'See what it means',
  caption: 'The ideas become specific to what they are experiencing.',
  src: "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/4wv71qfbbbwbngbvre9mbk/67ed1325-767e-4f35-bd1c-b49fa7a310ff/03_personalized_readout.png",
  chromeLabel: 'Prototype · See what it means',
  frameLine: 'The book’s ideas stop being abstract and start sounding like their week.',
  aspect: '4 / 3',
  eager: false
}, {
  n: '3',
  title: 'Choose a useful next move',
  caption: 'Insight becomes a concrete action rather than another unfinished idea.',
  src: "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/4wv71qfbbbwbngbvre9mbk/67ed1325-767e-4f35-bd1c-b49fa7a310ff/04_next_move_builder.png",
  chromeLabel: 'Prototype · Choose a next move',
  frameLine: 'Instead of another unfinished insight, they leave with a move they can make.',
  aspect: '4 / 3',
  eager: false
}] as const;
type Step = (typeof steps)[number];
function ProofLightbox({
  step,
  onClose
}: {
  step: Step;
  onClose: () => void;
}) {
  const titleId = useId();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  return <div className="fixed inset-0 z-[70] flex items-center justify-center bg-navy/80 p-4 backdrop-blur-sm md:p-8" role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={onClose} data-id="element-836" data-mp="205ipxkuh6cer">
      <div className="relative max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-2xl bg-paper shadow-[0_40px_80px_-20px_rgba(0,0,0,0.5)]" onClick={e => e.stopPropagation()} data-id="element-837" data-mp="1q61mzb6lycmh">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200/80 px-5 py-5 md:px-6 md:py-6" data-id="element-838" data-mp="1h7ydjd8wd6b1">
          <div className="min-w-0 max-w-3xl" data-id="element-839" data-mp="1m6jc788i3hxd">
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-text-muted" data-id="element-840" data-mp="3bl7e2fjgthh5">
              {step.title}
            </p>
            <p id={titleId} className="mt-2.5 font-serif text-xl leading-snug text-seaglass md:text-[1.65rem]" data-id="element-841" data-mp="3gipqwh0lsguf">
              {step.frameLine}
            </p>
          </div>
          <button type="button" onClick={onClose} className="flex-shrink-0 rounded-full p-2 text-text-muted transition-colors hover:bg-paper-dark hover:text-navy focus:outline-none focus:ring-2 focus:ring-seaglass" aria-label="Close preview" data-id="element-842" data-mp="3lpnrz3ddwoib">
            <X className="h-5 w-5" data-id="element-843" data-mp="3uo9k9q8nrhwa" />
          </button>
        </div>
        <div className="max-h-[calc(90vh-5rem)] overflow-auto bg-paper-dark p-3 md:p-5" data-id="element-844" data-mp="d802m8l23bdj">
          <img src={step.src} alt={`Simplify to Amplify prototype — ${step.title}`} className="mx-auto h-auto w-full rounded-lg shadow-sm" data-id="element-845" data-mp="8arezsh52f4w" />
        </div>
      </div>
    </div>;
}
export function PrototypeProof() {
  const [active, setActive] = useState<Step | null>(null);
  const openFirst = () => setActive(steps[0]);
  return <SectionBand tone="paperDark" width="wide" data-id="element-846" data-mp="2o1vwqexvzxdd">
      <div className="max-w-3xl" data-id="element-847" data-mp="2jzirrsqys3hn">
        <Reveal data-id="element-848" data-mp="2k0kbelm1pyaz">
          <Eyebrow data-id="element-849" data-mp="2k13krdxmrede">Real proof</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl leading-tight text-navy md:text-5xl" data-id="element-850" data-mp="2lgocnodegu88">
            What this looks like
            <br data-id="element-851" data-mp="2qf9cvnsnjdwo" />
            <span className="italic text-seaglass" data-id="element-852" data-mp="2qepyui64h9nx">in practice.</span>
          </h2>
          <span className="mt-5 inline-block rounded-full border border-gray-300/70 bg-paper px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-navy" data-id="element-853" data-mp="2uh4eyuiepvbi">
            Simplify to Amplify — working Reader-to-Revenue prototype
          </span>
        </Reveal>
      </div>

      <ProofOfBuild onExplore={openFirst} data-id="element-854" data-mp="26rifs721bg3i" />

      <div className="mt-14 flex flex-col gap-12 md:gap-16" data-id="element-855" data-mp="26sbfio2kt5c5">
        {steps.map((step, i) => <Reveal key={step.n} delay={0.05} data-id="element-856" data-mp="2bptp8gof3ol7" data-mpi={i}>
            <div className="grid items-center gap-6 md:grid-cols-2 md:gap-12" data-id="element-857" data-mp="2bp1sv3mzxmtv">
              <div className={i % 2 === 1 ? 'md:order-2' : ''} data-id="element-858" data-mp="2giwcy86ik3th">
                <div className="flex items-center gap-4" data-id="element-859" data-mp="1j160uc8dgj3f">
                  <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-seaglass font-serif text-lg text-white" data-id="element-860" data-mp="1o1aii5l0njv8">
                    {step.n}
                  </span>
                  <h3 className="font-serif text-2xl text-navy md:text-3xl" data-id="element-861" data-mp="1biom9rln2e3l">
                    {step.title}
                  </h3>
                </div>
                <p className="mt-4 max-w-md text-lg leading-relaxed text-text-muted" data-id="element-862" data-mp="16i0lwfbiskwp">
                  {step.caption}
                </p>
              </div>
              <div className={i % 2 === 1 ? 'md:order-1' : ''} data-id="element-863" data-mp="xindzr31o4e3">
                <button type="button" onClick={() => setActive(step)} className="group w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-seaglass focus-visible:ring-offset-2 focus-visible:ring-offset-paper-dark" aria-label={`Enlarge prototype: ${step.title}`} data-id="element-864" data-mp="xl03cpb4th16">
                  <span className="block transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:shadow-[0_28px_50px_-28px_rgba(26,40,56,0.45)]" data-id="element-865" data-mp="ivu3r08dtosr">
                    <DeviceFrame variant="browser" src={step.src} alt={`Simplify to Amplify prototype — ${step.title}`} chromeLabel={step.chromeLabel} aspect={step.aspect} loading={step.eager ? 'eager' : 'lazy'} data-id="element-866" data-mp="9udu4upe5qpl" />
                  </span>
                  <span className="mt-3 block text-center text-sm font-medium text-navy/55 transition-colors group-hover:text-navy/80" data-id="element-867" data-mp="457l4crjyk2h">
                    Click to enlarge
                  </span>
                </button>
              </div>
            </div>
          </Reveal>)}
      </div>

      {active && <ProofLightbox step={active} onClose={() => setActive(null)} data-id="element-868" data-mp="iyb4mpopf7vu" />}
    </SectionBand>;
}